import React, { useMemo } from 'react';
import Icon from './Icon';
import { loadGuidelines } from '../services/GuidelinesService';
import { loadTasks, fmtDueDate } from '../services/TasksService';
import { loadProjectEvents } from '../services/ProjectEventsService';
import { VendorCredit } from '../data/vendorData';
import { vendorTaskStatusColors, vendorTaskBlockedOnColors } from '../constants/statusColors';
import StatusBadge from './ui/StatusBadge';
import '../styles/components/VendorBrief.css';
import EmptyState from './EmptyState';

interface VendorBriefProps {
  vendorId: string;
  vendorTeamTag?: string;
  credits?: VendorCredit[];
}

const VendorBrief: React.FC<VendorBriefProps> = ({ vendorId, vendorTeamTag, credits }) => {
  const guidelines = useMemo(() => {
    return loadGuidelines().filter(g =>
      !g.archived && (
        g.vendor_ids?.includes(vendorId) ||
        (vendorTeamTag && g.team_tag === vendorTeamTag)
      )
    );
  }, [vendorId, vendorTeamTag]);

  const tasks = useMemo(() => {
    return loadTasks().filter(t =>
      !t.archived && (
        t.vendorIds?.includes(vendorId) ||
        (vendorTeamTag && t.teamTag === vendorTeamTag)
      )
    );
  }, [vendorId, vendorTeamTag]);

  const timelineEntries = useMemo(() => {
    return loadProjectEvents().filter(e =>
      e.vendorIds?.includes(vendorId)
    );
  }, [vendorId]);

  const hasCredits = credits && credits.length > 0;
  const hasContent = guidelines.length > 0 || tasks.length > 0 || timelineEntries.length > 0 || hasCredits;

  if (!hasContent) {
    return (
      <section className="side-section">
        <h4>Vendor Brief</h4>
        <EmptyState
          compact
          title="Nothing linked to this vendor yet"
          description="Link guidelines, activities or timeline items by setting their vendor/team tag."
        />
      </section>
    );
  }

  return (
    <>
      {/* Guidelines */}
      {guidelines.length > 0 && (
        <section className="side-section">
          <h4>Guidelines</h4>
          {guidelines.map(g => (
            <div key={g.id} className="vb-guideline">
              <p className="vb-guideline-title">
                {g.title}
                <span className="vb-guideline-cat">{g.category}</span>
              </p>
              <ul className="vb-list">
                {g.items.map(item => (
                  <li
                    key={item.id}
                    className={`vb-list-item ${item.emphasis ? 'vb-list-item--emphasis' : 'vb-list-item--normal'}`}
                  >
                    {item.emphasis && <strong className="vb-emphasis-marker">!</strong>} {item.text}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      {/* Schedule */}
      {timelineEntries.length > 0 && (
        <section className="side-section">
          <h4>Schedule</h4>
          <div className="vb-entry-list">
            {timelineEntries
              .sort((a, b) => a.startTime.localeCompare(b.startTime))
              .map(entry => (
                <div key={entry.id} className="vb-entry">
                  <span className="vb-entry-time">{entry.startTime}–{entry.endTime}</span>
                  <span className="vb-entry-title">{entry.title}</span>
                  <span className="vb-entry-location">{entry.location}</span>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Tasks */}
      {tasks.length > 0 && (
        <section className="side-section">
          <h4>Tasks</h4>
          <div className="vb-entry-list">
            {tasks.map(task => (
              <div key={task.id} className="vb-entry">
                <span className={`vb-priority ${task.priority === 'P0' ? 'vb-priority--p0' : task.priority === 'P1' ? 'vb-priority--p1' : 'vb-priority--p2'}`}>
                  {task.priority}
                </span>
                <span className="vb-task-name">{task.task}</span>
                {/* dynamic - exempt from guardrail: status color is driven by vendorTaskStatusColors map from data */}
                <StatusBadge color={vendorTaskStatusColors[task.status] || 'var(--color-muted)'}>
                  {task.status}
                </StatusBadge>
                {task.blockedOn && (
                  <StatusBadge color={vendorTaskBlockedOnColors[task.blockedOn] || 'var(--color-muted)'}>
                    Blocked ({task.blockedOn})
                  </StatusBadge>
                )}
                {task.dueDateISO && (
                  <span className="vb-task-date">{fmtDueDate(task.dueDateISO)}</span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Credits */}
      {hasCredits && (
        <section className="side-section">
          <h4>Credits</h4>
          <div className="vb-entry-list">
            {credits!.map((credit, i) => (
              <div key={i} className="vb-entry">
                <span className="vb-credit-role">{credit.role}</span>
                <a
                  href={credit.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vb-credit-handle"
                >
                  @{credit.handle}
                </a>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
};

export default VendorBrief;
